import { memo } from "react";
import { isDocContent } from "@/components/shapes/doc-frame/doc-content";
import { cn } from "@/lib/cn";
import type { FrameData } from "@/lib/frame-data";
import { previewSource } from "@/lib/frame-preview-source";
import { cardPreviews, type CardPreviewDensity } from "../card-previews";
import { GenericCardPreview } from "./generic-card-preview";

interface CardPreviewComponentProps {
  frame: FrameData;
  density: CardPreviewDensity;
}

/** Metadata a preview shows (HTML posters) or binds by (a chat's conversation). */
const PREVIEW_METADATA_KEYS = [
  "thumbnail",
  "conversationId",
  "conversation_id",
] as const;

function CardPreviewBody({ frame, density }: CardPreviewComponentProps) {
  const source = previewSource(frame);
  // Any V4 content opens as a Doc, so its card is the Doc's.
  const type = isDocContent(source) ? "doc" : frame.frameType;
  const Preview = cardPreviews[type] ?? GenericCardPreview;
  return (
    <div
      className={cn(
        // Reused frame parts read the text tokens; inside a card they are the card's.
        "[--color-muted:var(--card-muted)] [--color-primary:var(--card-ink)] [--color-secondary:var(--card-body)] [--color-subtle:var(--card-muted)]",
        // One type scale: the wall's column sets `--wall-fs`; sizes inside are em.
        "flex min-w-0 flex-col overflow-hidden text-card-ink [font-size:var(--wall-fs,13px)] leading-[1.45]",
        density === "compact" && "h-full",
      )}
      data-testid={`card-preview-${frame.frameType}`}
      data-density={density}
      inert
    >
      <Preview frame={frame} source={source} density={density} />
    </div>
  );
}

/** Re-render only when what a preview shows can change. */
function samePreview(
  previous: CardPreviewComponentProps,
  next: CardPreviewComponentProps,
): boolean {
  if (previous.density !== next.density) return false;
  const before = previous.frame;
  const after = next.frame;
  if (before === after) return true;
  return (
    before.frameId === after.frameId &&
    before.frameType === after.frameType &&
    before.title === after.title &&
    before.status === after.status &&
    PREVIEW_METADATA_KEYS.every(
      (key) => before.metadata?.[key] === after.metadata?.[key],
    ) &&
    previewSource(before) === previewSource(after)
  );
}

/**
 * A frame's static card: its type's preview (or the generic one) over
 * `previewSource(frame)`. The container owns the surface, the look and the
 * label; the preview is a picture (`inert`: nothing in it takes focus or
 * clicks, which pass through to the container).
 */
export const CardPreview = memo(CardPreviewBody, samePreview);
